<template>
    <section class="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <!-- Header avec logo -->
        <div class="w-full max-w-md flex justify-between items-center mb-8">
            <div class="flex items-center">
                <img src="../assets/logo.jpg" alt="Logo" class="w-10 h-10 rounded-md object-cover mr-3" />
                <h1 class="text-xl font-semibold text-gray-800">Pomme City</h1>
            </div>
            <div class="flex space-x-4">
                <button class="text-gray-500 hover:text-gray-700 transition-colors">
                    <i class="fas fa-circle-info"></i>
                </button>
                <button class="text-gray-500 hover:text-gray-700 transition-colors">
                    <i class="fas fa-circle-question"></i>
                </button>
            </div>
        </div>

        <!-- Carte de connexion -->
        <div class="w-full max-w-md bg-white rounded-xl shadow-sm p-8">
            <h2 class="text-2xl font-bold text-center text-gray-800 mb-8">Content de vous revoir</h2>
            
            <form @submit.prevent="login" class="space-y-6">
                <!-- Champ Email -->
                <div class="space-y-1">
                    <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
                    <input 
                        type="email" 
                        id="email" 
                        v-model="email" 
                        required
                        class="w-full px-4 py-3 outline-none border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                        placeholder="votre@email.com"
                    />
                </div>

                <!-- Champ Mot de passe avec œil -->
                <div class="space-y-1">
                    <div class="flex justify-between items-center">
                        <label for="password" class="block text-sm font-medium text-gray-700">Mot de passe</label>
                        <button 
                            type="button" 
                            @click="gotoResetPage" 
                            class="text-sm font-medium text-orange-600 hover:text-orange-500 hover:underline"
                        >
                            Mot de passe oublié ?
                        </button>
                    </div>
                    <div class="relative">
                        <input 
                            :type="showPassword ? 'text' : 'password'"
                            id="password" 
                            v-model="password" 
                            required
                            class="w-full px-4 py-3 outline-none border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all pr-10"
                            placeholder="••••••••"
                        />
                        <button
                            type="button"
                            @click="showPassword = !showPassword"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                        >
                            <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                        </button>
                    </div>
                </div>

                <!-- Bouton de connexion -->
                <button 
                    type="submit" 
                    :disabled="isLoading"
                    class="w-full py-3 px-4 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center"
                >
                    <span v-if="!isLoading">Se connecter</span>
                    <span v-else class="flex items-center">
                        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Connexion...
                    </span>
                </button>

                <!-- Message d'erreur -->
                <div v-if="error" class="text-red-500 text-sm text-center p-3 bg-red-50 rounded-lg">
                    {{ error }}
                </div>
            </form>
        </div>

        <!-- Footer -->
        <div class="mt-8 text-center text-sm text-gray-500">
            <p>© 2024 Pomme City. Tous droits réservés. </p>
        </div>
    </section>
</template>

<script setup>
import { useAuthStore } from '@/store/auth';
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { useRouter } from 'vue-router';
const router = useRouter();

const email = ref('');
const password = ref('');
const showPassword = ref(false);
const isLoading = ref(false);
const error = ref(null);

function gotoResetPage() {
  router.push('/reset')
}

const authStore = useAuthStore();
const user = computed(() => authStore.user);

const login = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    const response = await axios.post('/api/login', { 
      email: email.value, 
      password: password.value 
    });
    authStore.setToken(response.data.token);
    authStore.setUser(response.data.user);
    router.push("/dashboard")
  } catch (err) {
    if (err.response?.data?.error) {
      error.value = err.response.data.error;
    } else {
      error.value = 'Une erreur est survenue lors de la connexion';
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  if(user.value) {
    router.push("/dashboard")
  }
})
</script>

<style>
/* Animation pour le spinner */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>